import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  LayoutDashboard, 
  Users, 
  Briefcase, 
  UserCog, 
  Building, 
  DollarSign, 
  FileText, 
  TrendingUp, 
  ArrowLeftRight, 
  CreditCard,
  FileSignature,
  ClipboardList,
  Package,
  Wrench,
  Calculator,
  CalendarDays,
  Receipt,
  Target,
  HardHat,
  Sun,
  KanbanSquare,
  BarChart3,
  Settings,
  FolderCog,
  CircleDollarSign,
  FileBarChart,
  Shield,
  Trash2
} from "lucide-react";

// Ícone por módulo (mesma referência usada no menu lateral)
const moduleIcons = {
  // Operacional
  ordens_servico: ClipboardList,
  cronogramas: CalendarDays,
  cronogramas_dashboard: LayoutDashboard,
  // Comercial
  meu_dia: Sun,
  crm: KanbanSquare,
  comercial_dashboard: BarChart3,
  propostas: Receipt,
  contratos: FileSignature,
  precificacao: Calculator,
  comercial_config: Settings,
  // Cadastros
  clientes_fornecedores: Building,
  funcionarios: UserCog,
  cargos: Briefcase,
  usuarios: Users,
  estoque: Package,
  patrimonio: Wrench,
  // Financeiro
  financeiro_geral: TrendingUp,
  contas_pagar_receber: CreditCard,
  cartoes_credito: CreditCard,
  categorias_financeiras: CircleDollarSign,
  centro_custo: CircleDollarSign,
  plano_contas: FileText,
  dre: FileBarChart,
  dre_contrato: FileBarChart,
  fluxo_caixa: ArrowLeftRight,
  // Administração
  seguranca: Shield,
  limpeza_admin: Trash2,
};

// Grupos seguem EXATAMENTE a ordem do menu lateral (admin)
const moduleGroups = [
  {
    name: "Operacional",
    icon: HardHat,
    modules: ["ordens_servico", "cronogramas", "cronogramas_dashboard"]
  },
  {
    name: "Comercial",
    icon: Target,
    modules: ["meu_dia", "crm", "comercial_dashboard", "propostas", "contratos", "precificacao", "comercial_config"]
  },
  {
    name: "Cadastros",
    icon: FolderCog,
    modules: ["clientes_fornecedores", "funcionarios", "cargos", "usuarios", "estoque", "patrimonio"]
  },
  {
    name: "Financeiro",
    icon: DollarSign,
    modules: ["financeiro_geral", "contas_pagar_receber", "cartoes_credito", "categorias_financeiras", "centro_custo", "plano_contas", "dre", "dre_contrato", "fluxo_caixa"]
  },
  {
    name: "Administração",
    icon: Shield,
    modules: ["seguranca", "limpeza_admin"]
  }
];

const permissionLabels = {
  visualizar: "Ver",
  inserir: "Inserir",
  editar: "Editar",
  excluir: "Excluir"
};

export default function PermissionsMatrix({ 
  modules, 
  permissionTypes, 
  permissions, 
  onChange,
  disabled = false 
}) {
  const handlePermissionChange = (moduleKey, permType, checked) => {
    const currentPerms = permissions[moduleKey] || [];
    let newPerms;
    
    if (checked) {
      // When enabling a permission, also enable "visualizar" if not already
      newPerms = [...currentPerms];
      if (!newPerms.includes(permType)) {
        newPerms.push(permType);
      }
      // Always include "visualizar" when any permission is enabled
      if (permType !== "visualizar" && !newPerms.includes("visualizar")) {
        newPerms.push("visualizar");
      }
    } else {
      // When disabling "visualizar", disable all permissions for that module
      if (permType === "visualizar") {
        newPerms = [];
      } else {
        newPerms = currentPerms.filter(p => p !== permType);
      }
    }
    
    onChange({
      ...permissions,
      [moduleKey]: newPerms
    });
  };

  const handleModuleToggle = (moduleKey, enabled) => {
    if (enabled) {
      onChange({
        ...permissions,
        [moduleKey]: ["visualizar"]
      });
    } else {
      onChange({
        ...permissions,
        [moduleKey]: []
      });
    }
  };

  const handleGroupToggle = (groupModules, permType, enabled) => {
    const newPermissions = { ...permissions };
    groupModules.forEach(moduleKey => {
      const currentPerms = newPermissions[moduleKey] || [];
      if (enabled) {
        if (!currentPerms.includes(permType)) {
          newPermissions[moduleKey] = [...currentPerms, permType];
        }
        if (permType !== "visualizar" && !currentPerms.includes("visualizar")) {
          newPermissions[moduleKey] = [...(newPermissions[moduleKey] || []), "visualizar"];
        }
      } else {
        if (permType === "visualizar") {
          newPermissions[moduleKey] = [];
        } else {
          newPermissions[moduleKey] = currentPerms.filter(p => p !== permType);
        }
      }
    });
    onChange(newPermissions);
  };

  return (
    <div className="space-y-4" data-testid="permissions-matrix">
      {moduleGroups.map((group) => {
        const GroupIcon = group.icon || FileText;
        return (
        <div key={group.name} className="border rounded-lg overflow-hidden">
          <div className="bg-slate-100 px-4 py-2 font-semibold text-slate-700 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <GroupIcon className="h-4 w-4 text-luvcorp-navy" />
              {group.name}
            </span>
            <div className="flex gap-4">
              {permissionTypes.map((permType) => (
                <button
                  key={permType}
                  type="button"
                  onClick={() => {
                    const allHave = group.modules.every(m => 
                      (permissions[m] || []).includes(permType)
                    );
                    handleGroupToggle(group.modules, permType, !allHave);
                  }}
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  disabled={disabled}
                >
                  {permissionLabels[permType]}
                </button>
              ))}
            </div>
          </div>
          <div className="divide-y">
            {group.modules.map((moduleKey) => {
              const Icon = moduleIcons[moduleKey] || FileText;
              const modulePerms = permissions[moduleKey] || [];
              const hasAnyPermission = modulePerms.length > 0;
              
              return (
                <div 
                  key={moduleKey} 
                  className={`flex items-center px-4 py-3 ${hasAnyPermission ? 'bg-white' : 'bg-slate-50'}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                    <Icon className={`h-4 w-4 ${hasAnyPermission ? 'text-luvcorp-navy' : 'text-slate-400'}`} />
                    <span className={`text-sm ${hasAnyPermission ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>
                      {modules[moduleKey]}
                    </span>
                  </div>
                  <div className="flex items-center gap-6">
                    {permissionTypes.map((permType) => {
                      const isChecked = modulePerms.includes(permType);
                      const isDisabled = disabled || (permType !== "visualizar" && !modulePerms.includes("visualizar"));
                      
                      return (
                        <div key={permType} className="flex items-center gap-2 w-20">
                          <Checkbox
                            id={`${moduleKey}-${permType}`}
                            checked={isChecked}
                            onCheckedChange={(checked) => handlePermissionChange(moduleKey, permType, checked)}
                            disabled={isDisabled}
                            data-testid={`perm-${moduleKey}-${permType}`}
                          />
                          <Label 
                            htmlFor={`${moduleKey}-${permType}`}
                            className={`text-xs cursor-pointer ${isDisabled ? 'text-slate-300' : 'text-slate-600'}`}
                          >
                            {permissionLabels[permType]}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })}
      
      <div className="text-xs text-slate-500 p-2 bg-slate-50 rounded">
        💡 Dica: Clique nos títulos das colunas para aplicar/remover permissão em todo o grupo
      </div>
    </div>
  );
}
